/**
 * MobileEmployeeCard Component
 *
 * Card-based display for employee data on mobile devices.
 * Replaces the DataTable view for better touch interaction.
 */

import React, { useState } from 'react';
import { Box, Typography, Skeleton, IconButton, Menu, MenuItem } from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { colors, typography as typographyTokens, radii, shadows } from '@components/design-system/tokens';
import { StatusBadge } from '@components/design-system';
import type { EmployeeSearchResult } from '@/types/newEmployees';

interface MobileEmployeeCardProps {
    employee: EmployeeSearchResult;
    onClick?: () => void;
    onDelete?: () => void;
}

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
        <Typography
            sx={{
                fontSize: typographyTokens.fontSize.xs,
                color: colors.neutral[500],
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
            }}>
            {label}
        </Typography>
        <Typography
            sx={{
                fontSize: typographyTokens.fontSize.sm,
                color: colors.neutral[700],
                fontWeight: typographyTokens.fontWeight.medium,
                textAlign: 'right',
                maxWidth: '60%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}>
            {value || '-'}
        </Typography>
    </Box>
);

export const MobileEmployeeCard: React.FC<MobileEmployeeCardProps> = ({ employee, onClick, onDelete }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => setAnchorEl(null);

    const handleDelete = () => {
        handleMenuClose();
        onDelete?.();
    };

    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'block',
                textDecoration: 'none',
                backgroundColor: colors.neutral[0],
                borderRadius: `${radii.lg}px`,
                border: `1px solid ${colors.neutral[200]}`,
                p: 2,
                mb: 1.5,
                boxShadow: shadows.card,
                transition: 'all 150ms ease',
                cursor: onClick ? 'pointer' : 'default',
                '&:hover': {
                    boxShadow: shadows.elevated,
                    borderColor: colors.primary[200],
                },
                '&:active': onClick
                    ? {
                          transform: 'scale(0.99)',
                      }
                    : undefined,
            }}>
            {/* Header with name and status */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: `${radii.md}px`,
                        backgroundColor: `${colors.warning.main}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                    <BadgeIcon sx={{ color: colors.warning.main, fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.md,
                                fontWeight: typographyTokens.fontWeight.semibold,
                                color: colors.neutral[900],
                            }}>
                            {employee.Name}
                        </Typography>
                        <StatusBadge
                            label={employee.Status || 'Unknown'}
                            status={employee.Status === 'Active' ? 'success' : 'neutral'}
                            size='small'
                        />
                    </Box>
                    {employee.Email && (
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.xs,
                                color: colors.neutral[500],
                                mt: 0.25,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                            {employee.Email}
                        </Typography>
                    )}
                </Box>

                {/* Actions menu */}
                {onDelete && (
                    <>
                        <IconButton size='small' onClick={handleMenuOpen} sx={{ ml: 'auto' }}>
                            <MoreVertIcon fontSize='small' sx={{ color: colors.neutral[500] }} />
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            onClick={(e) => e.stopPropagation()}
                            PaperProps={{ sx: { minWidth: 140 } }}>
                            <MenuItem onClick={handleDelete} dense sx={{ color: colors.error.main }}>
                                <DeleteOutlineIcon sx={{ mr: 1 }} fontSize='small' />
                                Delete
                            </MenuItem>
                        </Menu>
                    </>
                )}
            </Box>

            {/* Details */}
            <Box sx={{ borderTop: `1px solid ${colors.neutral[100]}`, pt: 1 }}>
                <InfoRow label='Location' value={employee.LocationName || 'None'} />
                <InfoRow label='Reseller' value={employee.ResellerName || 'None'} />
                <InfoRow label='Company' value={employee.ParentCompanyName || 'None'} />
            </Box>
        </Box>
    );
};

// Loading skeleton for mobile employee cards
export const MobileEmployeeCardSkeleton: React.FC = () => (
    <Box
        sx={{
            backgroundColor: colors.neutral[0],
            borderRadius: `${radii.lg}px`,
            border: `1px solid ${colors.neutral[200]}`,
            p: 2,
            mb: 1.5,
        }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
            <Skeleton variant='rounded' width={40} height={40} />
            <Box sx={{ flex: 1 }}>
                <Skeleton variant='text' width='60%' height={24} />
                <Skeleton variant='text' width='80%' height={16} />
            </Box>
        </Box>
        <Box sx={{ borderTop: `1px solid ${colors.neutral[100]}`, pt: 1 }}>
            <Skeleton variant='text' width='100%' height={20} />
            <Skeleton variant='text' width='100%' height={20} />
            <Skeleton variant='text' width='100%' height={20} />
        </Box>
    </Box>
);

export default MobileEmployeeCard;
